/**
 * Shoppable Video — Vanilla JS, no libraries.
 *
 * Modules:
 *   ShoppableVideoAnalytics  — dataLayer + CustomEvent for each user action
 *   ShoppableVideoCart       — theme-aligned AJAX add-to-cart (drawer + cart icon)
 *   ShoppableVideoPlayer     — fullscreen modal (PDP / landing page)
 *   ShoppableVideoCarousel   — homepage coverflow carousel
 *
 * Boot (DOMContentLoaded):
 *   1. buildVideoRegistry()          — parse all JSON data blocks → registry map
 *   2. attachModalCardDelegation()   — [data-sv-card] clicks → open modal
 *   3. attachAtcDelegation()         — [data-sv-add-to-cart] → theme cart flow
 *   4. setupLandingAutoplay()        — IntersectionObserver for vertical feed
 *   5. setupGridInteractions()       — grid layout mute/panel/autoplay
 *   6. ShoppableVideoCarousel.init() — for each [data-sv-carousel] on the page
 */

'use strict';

// ─────────────────────────────────────────────────────────────────────────────
// Analytics
// ─────────────────────────────────────────────────────────────────────────────

var ShoppableVideoAnalytics = {
  /* Push to window.dataLayer (GTM) AND fire a CustomEvent on document. */
  _fire: function (eventName, payload) {
    if (window.dataLayer) {
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
    }
    document.dispatchEvent(
      new CustomEvent(eventName, { detail: payload, bubbles: true })
    );
  },

  videoView:    function (id, title, influencer) { this._fire('sv_video_view',    { videoId: id, title: title, influencer: influencer }); },
  videoComplete:function (id, title)             { this._fire('sv_video_complete', { videoId: id, title: title }); },
  productClick: function (productId, videoId)    { this._fire('sv_product_click',  { productId: productId, videoId: videoId }); },
  addToCart:    function (variantId, videoId)    { this._fire('sv_add_to_cart',    { variantId: variantId, videoId: videoId }); },
};


// ─────────────────────────────────────────────────────────────────────────────
// Cart
// ─────────────────────────────────────────────────────────────────────────────

var ShoppableVideoCart = {
  addToCart: function (variantId, quantity) {
    quantity = quantity || 1;
    return fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify({ id: Number(variantId), quantity: quantity }),
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (e) { return Promise.reject(e); });
      return res.json();
    });
  },

  addWithTheme: function (variantId, quantity, options) {
    var self = this;
    options = options || {};
    quantity = quantity || 1;

    self._setTriggerLoading(options.triggerEl, true);
    self._setCartLoading(true);

    return this._postAddWithSections(variantId, quantity)
      .then(function (data) {
        return self._afterThemeAdd(data, quantity, options);
      })
      .catch(function (err) {
        self._setTriggerLoading(options.triggerEl, false);
        self._setCartLoading(false);
        self._showThemeError(err);
        return Promise.reject(err);
      });
  },

  _postAddWithSections: function (variantId, quantity) {
    var sections = 'cart-icon-bubble';
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      var drawerSection = cartDrawer.closest('.shopify-section');
      if (drawerSection) {
        sections += ',' + drawerSection.id.replace('shopify-section-', '');
      }
    }

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', String(quantity));
    formData.append('sections', sections);
    formData.append('sections_url', window.location.pathname);

    var cartAddUrl = (window.theme && theme.routes && theme.routes.cartAdd)
      ? theme.routes.cartAdd
      : '/cart/add';

    return fetch(cartAddUrl, {
      method:  'POST',
      headers: {
        Accept:                 'application/javascript',
        'X-Requested-With':     'XMLHttpRequest',
      },
      body: formData,
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var err = data.description || data.message || 'Could not add to cart';
          return Promise.reject({ description: err });
        }
        return data;
      });
    });
  },

  _afterThemeAdd: function (data, requestedQty, options) {
    var self = this;
    options = options || {};

    if (options.triggerEl) {
      self._setTriggerSuccess(options.triggerEl);
    }

    if (window.theme && theme.settings && theme.settings.vibrateOnATC && window.navigator.vibrate) {
      window.navigator.vibrate(100);
    }

    if (data.sections && data.sections['cart-icon-bubble']) {
      var cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
      }
    }

    var cartDrawer = document.querySelector('cart-drawer');
    var openDrawer = !!(cartDrawer && window.theme && theme.settings && (
      (theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches && theme.mediaMatches.md) ||
      theme.settings.afterAtc === 'drawer'
    ));

    var newCartPromise = fetch(
      (window.theme && theme.routes.cart) ? theme.routes.cart + '.js' : '/cart.js'
    ).then(function (r) { return r.json(); });

    var cartItems = document.querySelector('cart-items');
    var freebiePromise = (cartItems && typeof cartItems.checkAndHandleFreeProductImmediately === 'function')
      ? cartItems.checkAndHandleFreeProductImmediately(data)
      : Promise.resolve();

    var renderPromise = Promise.resolve();
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      renderPromise = cartDrawer.renderContents(data, openDrawer).then(function () {
        if (typeof CartDrawer !== 'undefined' && CartDrawer.recalculateCssVarHeights) {
          CartDrawer.recalculateCssVarHeights();
        }
      });
    } else if (window.location.pathname === ((window.theme && theme.routes.cart) || '/cart')) {
      if (cartItems) {
        if (cartItems.dataset.empty === 'true') {
          window.location.reload();
        } else if (typeof cartItems.refreshCartItems === 'function') {
          renderPromise = cartItems.refreshCartItems();
        }
      }
    }

    return Promise.all([renderPromise, freebiePromise.catch(function () {}), newCartPromise])
      .then(function (results) {
        var newCartData = results[2];

        self._setCartLoading(false);

        if (window.ccPublishCartSnapshot) {
          window.ccPublishCartSnapshot(newCartData, 'shoppable-video-atc');
        }

        if (typeof window.ccDispatchCartChange === 'function') {
          window.ccDispatchCartChange({
            cart:             newCartData,
            newTotalQuantity: newCartData.item_count,
            newTotalPrice:    newCartData.total_price,
            source:           'shoppable-video-atc',
          });
        } else {
          document.dispatchEvent(new CustomEvent('on:cart:change', {
            bubbles: true,
            detail: { cart: newCartData, cartUpdated: true, source: 'shoppable-video-atc' },
          }));
        }

        if (options.videoId) {
          ShoppableVideoAnalytics.addToCart(data.variant_id, options.videoId);
        }

        if (options.onSuccess) options.onSuccess(data);
        return data;
      });
  },

  _setTriggerLoading: function (el, loading) {
    if (!el) return;
    if (loading) {
      if (el.dataset.svWasDisabled === undefined) {
        el.dataset.svWasDisabled = el.disabled ? 'true' : 'false';
      }
      el.disabled = true;
      el.setAttribute('aria-busy', 'true');
      el.classList.add('is-loading');
      el.classList.remove('sv-atc-success');
    } else {
      el.classList.remove('is-loading', 'sv-atc-success');
      el.removeAttribute('aria-busy');
      el.disabled = el.dataset.svWasDisabled === 'true';
    }
  },

  _setTriggerSuccess: function (el) {
    if (!el) return;
    el.classList.remove('is-loading');
    el.classList.add('sv-atc-success');
    el.removeAttribute('aria-busy');
    var self = this;
    clearTimeout(el._svSuccessTimer);
    el._svSuccessTimer = setTimeout(function () {
      self._setTriggerLoading(el, false);
    }, 1100);
  },

  _setCartLoading: function (loading) {
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.toggle('pointer-events-none', loading);
      cartDrawer.querySelectorAll('.cart-item__loader').forEach(function (loader) {
        loader.hidden = !loading;
      });
    }
    if (window.location.pathname === ((window.theme && theme.routes.cart) || '/cart')) {
      var cartItems = document.querySelector('cart-items');
      if (cartItems) {
        cartItems.classList.toggle('pointer-events-none', loading);
        cartItems.querySelectorAll('.cart-item__loader').forEach(function (loader) {
          loader.hidden = !loading;
        });
      }
    }
  },

  _showThemeError: function (err) {
    var msg = (err && (err.description || err.message)) || 'Could not add to cart';
    this.showToast(msg, 'error');
  },

  /* Re-fetch /cart.js and update common cart-count elements in the header.
     Fails silently — non-critical. */
  updateCartCount: function () {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count;
        [
          '[data-cart-count]', '.cart-count', '.cart__count', '#cart-count',
          '[id*="cart-count"]', '[class*="cart-count"]', '.header__cart-count',
          'cart-icon-bubble .visually-hidden', '.cart-count-bubble',
        ].forEach(function (sel) {
          document.querySelectorAll(sel).forEach(function (el) {
            el.textContent = count;
            if (el.hasAttribute('data-cart-count')) el.setAttribute('data-cart-count', count);
          });
        });
      })
      .catch(function () {});
  },

  showToast: function (message, type) {
    var toast = document.getElementById('sv-toast');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.toggle('is-error', type === 'error');
    toast.classList.add('is-visible');
    clearTimeout(toast._svTimer);
    toast._svTimer = setTimeout(function () {
      toast.classList.remove('is-visible', 'is-error');
    }, 3000);
  },
};


// ─────────────────────────────────────────────────────────────────────────────
// Modal Player — used by PDP snippet and landing page product chips
// ─────────────────────────────────────────────────────────────────────────────

var ShoppableVideoPlayer = {
  _feed: [],
  _currentIndex: 0,
  _touchStartY: 0,
  _els: null,
  _listenersAttached: false,
  _currentVideoId: null,

  _cacheEls: function () {
    if (this._els) return;
    this._els = {
      overlay:          document.getElementById('sv-modal-overlay'),
      video:            document.getElementById('sv-modal-video'),
      close:            document.getElementById('sv-modal-close'),
      navUp:            document.getElementById('sv-nav-up'),
      navDown:          document.getElementById('sv-nav-down'),
      productList:      document.getElementById('sv-product-list'),
      influencerName:   document.getElementById('sv-modal-influencer-name'),
      influencerHandle: document.getElementById('sv-modal-influencer-handle'),
      title:            document.getElementById('sv-modal-title'),
    };
  },

  open: function (videoData, allVideos, startIndex, modalOpts) {
    this._cacheEls();
    this._modalOpts     = modalOpts || {};
    this._feed         = allVideos  || [videoData];
    this._currentIndex = startIndex || 0;
    this._loadVideo(videoData);

    this._els.overlay.removeAttribute('hidden');
    this._els.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    if (!this._listenersAttached) {
      this._attachListeners();
      this._listenersAttached = true;
    }
  },

  _loadVideo: function (videoData) {
    this._cacheEls();
    var els = this._els;
    this._currentVideoId = videoData.id;

    els.video.pause();
    els.video.loop   = true;
    els.video.src    = (videoData.videoUrl || '').trim();
    var poster = (videoData.thumbnail || '').trim();
    if (poster) {
      els.video.poster = poster;
    } else {
      els.video.removeAttribute('poster');
    }
    els.video.load();
    els.video.play().catch(function () {});

    els.influencerName.textContent   = videoData.influencerName   || '';
    els.influencerHandle.textContent = videoData.influencerHandle ? '@' + videoData.influencerHandle : '';
    els.title.textContent            = videoData.title || '';

    els.navUp.disabled   = this._currentIndex <= 0;
    els.navDown.disabled = this._currentIndex >= this._feed.length - 1;

    this._renderProducts(videoData.products || [], videoData.id, this._modalOpts);
    ShoppableVideoAnalytics.videoView(videoData.id, videoData.title, videoData.influencerName);

    var id = videoData.id, title = videoData.title;
    els.video.onended = function () { ShoppableVideoAnalytics.videoComplete(id, title); };
  },

  _renderProducts: function (products, videoId, opts) {
    opts = opts || {};
    var showPrice = opts.showPrice !== false;
    var showAtc   = opts.showAtc !== false;
    var list    = this._els.productList;
    var heading = list.querySelector('.sv-product-list-heading');
    list.innerHTML = '';
    if (heading) list.appendChild(heading);

    if (!products.length) {
      var msg = document.createElement('p');
      msg.style.cssText = 'font-size:0.78rem;opacity:0.55;padding:6px 0;';
      msg.textContent   = 'No products tagged in this video.';
      list.appendChild(msg);
      return;
    }

    products.forEach(function (p) {
      var card  = document.createElement('div');
      card.className          = 'sv-product-card';
      card.dataset.productId  = p.id;
      card.dataset.variantId  = p.variantId;
      card.dataset.videoId    = videoId;

      var img       = document.createElement('img');
      img.className = 'sv-product-card-image';
      img.src       = p.image || '';  img.alt = p.title || '';
      img.width     = 56;             img.height = 56;
      img.loading   = 'lazy';

      var info   = document.createElement('div');
      info.className = 'sv-product-card-info';

      var t = document.createElement('p');
      t.className   = 'sv-product-card-title';
      t.textContent = p.title || '';

      var pr = document.createElement('p');
      pr.className   = 'sv-product-card-price';
      pr.textContent = p.price || '';

      info.appendChild(t);
      if (showPrice) info.appendChild(pr);

      card.appendChild(img);
      card.appendChild(info);

      if (showAtc) {
        var btn             = document.createElement('button');
        btn.className       = 'sv-product-card-btn';
        btn.dataset.svAddToCart = '';
        btn.dataset.variantId   = p.variantId;
        btn.dataset.videoId     = videoId;
        btn.disabled            = !p.available;
        if (!p.available) btn.dataset.svWasDisabled = 'true';
        var label = document.createElement('span');
        label.className   = 'sv-atc-btn__label';
        label.textContent = p.available ? 'Add to cart' : 'Sold out';
        btn.appendChild(label);
        card.appendChild(btn);
      }

      card.addEventListener('click', function (e) {
        if (!e.target.closest('[data-sv-add-to-cart]')) {
          ShoppableVideoAnalytics.productClick(p.id, videoId);
        }
      });

      list.appendChild(card);
    });
  },

  _navigate: function (direction) {
    var next = direction === 'down' ? this._currentIndex + 1 : this._currentIndex - 1;
    if (next < 0 || next >= this._feed.length) return;
    this._currentIndex = next;
    this._loadVideo(this._feed[next]);
  },

  close: function () {
    this._cacheEls();
    this._els.video.pause();
    this._els.video.src = '';
    this._els.overlay.classList.remove('is-open');
    this._els.overlay.setAttribute('hidden', '');
    document.body.style.overflow = '';
    this._currentVideoId = null;
  },

  /* Event listeners wired once — NOTE: ATC is handled by the document-wide
     attachAtcDelegation() in the boot, not here. */
  _attachListeners: function () {
    var self = this, els = this._els;

    els.overlay.addEventListener('click', function (e) {
      if (e.target === els.overlay) self.close();
    });
    els.close.addEventListener('click', function () { self.close(); });

    document.addEventListener('keydown', function (e) {
      if (!els.overlay.classList.contains('is-open')) return;
      if (e.key === 'Escape')    self.close();
      if (e.key === 'ArrowDown') self._navigate('down');
      if (e.key === 'ArrowUp')   self._navigate('up');
    });

    els.navUp.addEventListener('click',   function () { self._navigate('up');   });
    els.navDown.addEventListener('click', function () { self._navigate('down'); });

    els.overlay.addEventListener('touchstart', function (e) {
      self._touchStartY = e.changedTouches[0].clientY;
    }, { passive: true });

    els.overlay.addEventListener('touchend', function (e) {
      var dy = self._touchStartY - e.changedTouches[0].clientY;
      if (Math.abs(dy) < 50) return;
      self._navigate(dy > 0 ? 'down' : 'up');
    }, { passive: true });
  },
};


// ─────────────────────────────────────────────────────────────────────────────
// Carousel popup — homepage coverflow active-card click (not native fullscreen)
// ─────────────────────────────────────────────────────────────────────────────

var ShoppableVideoCarouselPopup = {
  _els: null,
  _listenersAttached: false,
  _escapeHandler: null,

  _cacheEls: function () {
    if (this._els) return;
    this._els = {
      overlay: document.getElementById('sv-carousel-popup-overlay'),
      dialog:  document.getElementById('sv-carousel-popup'),
      video:   document.getElementById('sv-carousel-popup-video'),
      close:   document.getElementById('sv-carousel-popup-close'),
    };
  },

  open: function (opts) {
    this._cacheEls();
    var els = this._els;
    if (!els.overlay || !els.video) return;

    els.video.pause();
    els.video.loop   = true;
    els.video.src    = (opts.src || '').trim();
    var poster = (opts.poster || '').trim();
    if (poster) {
      els.video.poster = poster;
    } else {
      els.video.removeAttribute('poster');
    }
    els.video.muted  = false;
    els.video.load();
    els.video.play().catch(function () {});

    els.overlay.removeAttribute('hidden');
    els.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    if (opts.videoId) {
      ShoppableVideoAnalytics.videoView(opts.videoId, opts.title || '', opts.influencer || '');
    }

    if (!this._listenersAttached) {
      this._attachListeners();
      this._listenersAttached = true;
    }
  },

  close: function () {
    this._cacheEls();
    var els = this._els;
    if (!els.overlay) return;

    els.video.pause();
    els.video.src = '';
    els.overlay.classList.remove('is-open');
    els.overlay.setAttribute('hidden', '');
    document.body.style.overflow = '';
  },

  _attachListeners: function () {
    var self = this;
    var els  = this._els;

    els.close.addEventListener('click', function () { self.close(); });

    els.overlay.addEventListener('click', function (e) {
      if (e.target === els.overlay) self.close();
    });

    els.dialog.addEventListener('click', function (e) {
      e.stopPropagation();
    });

    this._escapeHandler = function (e) {
      if (e.key === 'Escape' && els.overlay.classList.contains('is-open')) {
        self.close();
      }
    };
    document.addEventListener('keydown', this._escapeHandler);
  },
};


// ─────────────────────────────────────────────────────────────────────────────
// Progress bar — wire timeupdate once per video element
// ─────────────────────────────────────────────────────────────────────────────

function attachProgressBar(video, card) {
  var bar = card.querySelector('[data-sv-progress-bar]');
  if (!bar || video._svProgressAttached) return;
  video._svProgressAttached = true;
  video.addEventListener('timeupdate', function () {
    if (!video.duration) return;
    bar.style.width = (video.currentTime / video.duration * 100) + '%';
  });
}


// ─────────────────────────────────────────────────────────────────────────────
// Campaign config — read from [data-sv-campaign] root (values set by Liquid from theme customizer).
// ─────────────────────────────────────────────────────────────────────────────

function isRingLayout(layout) {
  return layout === 'coverflow' || layout === 'carousel';
}

function readCampaignConfig(el) {
  var root = (el && el.closest) ? (el.closest('[data-sv-campaign]') || el) : el;
  if (!root || !root.dataset) root = el;

  var ds = root.dataset;
  var cardWidthRaw = parseInt(ds.svCardWidth, 10);
  if (isNaN(cardWidthRaw) && ds.cardWidth !== undefined) {
    cardWidthRaw = parseInt(ds.cardWidth, 10);
  }

  var mobileWidthRaw = parseInt(ds.svMobileCardWidth, 10);
  if (isNaN(mobileWidthRaw)) mobileWidthRaw = -1;
  var mobileHeightRaw = parseInt(ds.svMobileCardHeight, 10);
  if (isNaN(mobileHeightRaw)) mobileHeightRaw = 0;
  var mobileGap = parseInt(ds.svMobileGap, 10);
  if (isNaN(mobileGap)) mobileGap = -1;
  var mobileCardsVisible = parseInt(ds.svMobileCardsVisible, 10);

  var layout = ds.svLayout || 'coverflow';

  return {
    layout:       layout,
    cardShape:    ds.svCardShape || 'portrait',
    cardWidth:    isNaN(cardWidthRaw) ? 0 : cardWidthRaw,
    cardHeight:   parseInt(ds.svCardHeight, 10) || parseInt(ds.cardHeight, 10) || 400,
    gap:          parseInt(ds.svGap, 10) || 16,
    cardsVisible: parseInt(ds.svCardsVisible, 10) || parseInt(ds.cardsVisible, 10) || 5,
    mobileCardWidth:    mobileWidthRaw,
    mobileCardHeight:   mobileHeightRaw,
    mobileGap:          mobileGap,
    mobileCardsVisible: isNaN(mobileCardsVisible) ? 0 : mobileCardsVisible,
    autoplay:     ds.svAutoplay !== 'false',
    defaultMuted: ds.svMuted !== 'false' && ds.defaultMuted !== 'false',
    openOnClick:  ds.svOpenOnClick === 'true' || ds.openOnClick === 'true',
    showPrice:    ds.svShowPrice !== 'false',
    showAtc:      ds.svShowAtc !== 'false',
    openNewTab:   ds.svNewTab === 'true',
    showProgress: ds.svShowProgress === 'true',
    loopVideos:   ds.svLoop !== 'false',
    maxItems:     parseInt(ds.svMaxItems, 10) || 0,
    autoscroll:   ds.svAutoscroll === 'true',
    playActiveOnly: isRingLayout(layout) || ds.svPlayActiveOnly === 'true',
    playbackSlot:   parseInt(ds.svPlaybackSlot, 10) || 0,
    startingVideo:  parseInt(ds.svStartingVideo, 10) || 1,
  };
}

function playbackOffsetFromSlot(slot, cardsVisible) {
  var cv = cardsVisible || 5;
  var centerSlot = Math.floor((cv + 1) / 2);
  var clamped = Math.max(1, Math.min(slot || centerSlot, cv));
  return clamped - centerSlot;
}

function activeIndexForStartingVideo(startVideoIndex, playbackOffset, total) {
  if (!total) return 0;
  var startIdx = ((startVideoIndex % total) + total) % total;
  var active = startIdx - playbackOffset;
  return ((active % total) + total) % total;
}

function isSvMobileViewport() {
  return typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(max-width: 767px)').matches;
}

/* Resolve desktop vs mobile layout sizes. Mobile width 0 / -1 falls back to desktop. */
function resolveCampaignLayout(cfg) {
  cfg = cfg || {};
  var mobile = isSvMobileViewport();
  var cardWidth = cfg.cardWidth || 0;
  var cardHeight = cfg.cardHeight || 400;
  var gap = cfg.gap || 16;
  var cardsVisible = cfg.cardsVisible || 5;

  if (mobile) {
    if (cfg.mobileCardWidth > 0) cardWidth = cfg.mobileCardWidth;
    if (cfg.mobileCardHeight > 0) cardHeight = cfg.mobileCardHeight;
    if (cfg.mobileGap >= 0) gap = cfg.mobileGap;
    if (cfg.mobileCardsVisible >= 3) cardsVisible = cfg.mobileCardsVisible;
  }

  return {
    cardWidth: cardWidth,
    cardHeight: cardHeight,
    gap: gap,
    cardsVisible: cardsVisible,
    isMobile: mobile,
  };
}

function readModalOpts(el) {
  var cfg = readCampaignConfig(el);
  return { showPrice: cfg.showPrice, showAtc: cfg.showAtc };
}


// ─────────────────────────────────────────────────────────────────────────────
// Coverflow Carousel
// ─────────────────────────────────────────────────────────────────────────────

var ShoppableVideoCarousel = {
  /**
   * Initialise one carousel instance.
   *
   * @param {Element} wrapEl   — [data-sv-carousel] element (the .sv-coverflow-wrap)
   * @param {object}  registry — video data registry from buildVideoRegistry()
   */
  init: function (wrapEl, registry) {
    /* ── State ── */
    var self         = this._createInstance();
    self._wrap       = wrapEl;
    self._clip       = wrapEl.querySelector('.sv-coverflow-clip');
    self._track      = wrapEl.querySelector('.sv-coverflow-track');
    self._registry   = registry;
    self._realCards  = Array.from(self._track.querySelectorAll('[data-sv-carousel-card]'));
    self._realCount  = self._realCards.length;

    if (!self._realCount) return;

    self._cards = self._realCards;

    /* Read campaign config from [data-sv-campaign] root (backward compat: wrap attrs) */
    self._config        = readCampaignConfig(wrapEl);
    var layoutSizes     = resolveCampaignLayout(self._config);
    self._cardsVisible  = layoutSizes.cardsVisible;
    self._defaultMuted  = self._config.defaultMuted;
    self._openOnClick   = self._config.openOnClick;
    self._autoplay      = self._config.autoplay;
    self._playActiveOnly = isRingLayout(self._config.layout) || self._config.playActiveOnly;
    self._isCarousel     = self._config.layout === 'carousel';
    self._campaignRoot  = wrapEl.closest('[data-sv-campaign]') || wrapEl;
    self._applyPlaybackConfig(layoutSizes);
    self._activeIndex   = self._initialActiveIndex();
    self._touchStartX   = 0;
    self._isAnimating   = false;
    self._prevPositions = {};
    self._focusStepTimer = null;
    self._focusTarget    = null;
    self._playbackTimer  = null;
    self._slideTimer     = null;

    /* Apply gap CSS variable on campaign root (viewport-aware) */
    self._campaignRoot.style.setProperty('--sv-visual-gap', layoutSizes.gap + 'px');

    /* ── Compute widths + initial positions after first paint ── */
    self._pauseAll();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self._computeCardWidth();
        self._update(false);
        self._wrap.classList.add('is-ready');
        if (self._autoplay) {
          self._pauseAll();
          if (self._shouldSinglePlayback()) {
            self._syncActivePlayback();
            self._warmCarouselVideos();
          } else {
            self._playAll();
          }
        }
        self._setupLazyLoad();
      });
    });

    /* ── Wire nav buttons (scoped to parent <section>) ── */
    var section = wrapEl.closest('section') || document;
    var prevBtn = section.querySelector('[data-sv-carousel-prev]');
    var nextBtn = section.querySelector('[data-sv-carousel-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { self._prev(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { self._next(); });

    /* ── Card clicks — inactive cards focus only; active card is interactive ── */
    self._track.addEventListener('click', function (e) {
      var card = e.target.closest('[data-sv-carousel-card]');
      if (!card) return;

      var realIndex = self._realIndexForCard(card);
      if (realIndex === -1) return;

      if (realIndex !== self._activeIndex) {
        e.preventDefault();
        self._focusCard(realIndex);
        return;
      }

      if (e.target.closest('[data-sv-product-panel]')) {
        if (e.target.closest('[data-sv-panel-toggle]')) {
          var panel  = card.querySelector('[data-sv-product-panel]');
          if (!panel) return;
          var btn    = e.target.closest('[data-sv-panel-toggle]');
          var drawer = panel.querySelector('[data-sv-panel-drawer]');
          if (!drawer) return;
          var isOpen = drawer.classList.contains('is-open');
          drawer.classList.toggle('is-open', !isOpen);
          drawer.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
          btn.setAttribute('aria-expanded', String(!isOpen));
          btn.classList.toggle('is-open', !isOpen);
        }
        return;
      }

      if (e.target.closest('[data-sv-mute-btn]')) {
        e.stopPropagation();
        self._toggleMute(e.target.closest('[data-sv-mute-btn]').dataset.videoId);
        return;
      }

      if (self._openOnClick && e.target.closest('.sv-card-video-wrap')) {
        e.preventDefault();
        self._openPopup(self._realCards[realIndex]);
      }
    });

    self._track.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var card = e.target.closest('[data-sv-carousel-card]');
      if (!card) return;
      e.preventDefault();
      card.click();
    });

    /* ── Wire touch swipe on the track ── */
    self._track.addEventListener('touchstart', function (e) {
      self._touchStartX = e.changedTouches[0].clientX;
    }, { passive: true });

    self._track.addEventListener('touchend', function (e) {
      var dx = self._touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(dx) < 40) return;
      if (dx > 0) self._next(); else self._prev();
    }, { passive: true });

    /* ── Recalculate on window resize ── */
    window.addEventListener('resize', function () {
      self._computeCardWidth();
      self._update(false);
    });
  },

  /* Factory: create a plain object per carousel instance so multiple
     carousels on the same page don't share state. */
  _createInstance: function () {
    return Object.create(ShoppableVideoCarousel._proto);
  },
};

/* Instance methods, shared via prototype (no state stored here) */
ShoppableVideoCarousel._proto = {

  /* ── Sizing ──────────────────────────────────────────────────────── */

  /* Compute card width from the wrap's current pixel width and the
     cards_visible setting. Cards are absolutely centred on the track. */
  _computeCardWidth: function () {
    var cfg        = this._config || readCampaignConfig(this._wrap);
    var layout     = resolveCampaignLayout(cfg);
    var fixedW     = layout.cardWidth;
    var fixedH     = layout.cardHeight;
    this._isCarousel = cfg.layout === 'carousel';
    this._applyPlaybackConfig(layout);
    this._cardsVisible = layout.cardsVisible;
    var clipWidth  = (this._clip || this._wrap).getBoundingClientRect().width;
    var cardWidth  = fixedW > 0 ? fixedW : Math.floor(clipWidth / this._cardsVisible);
    var videoMaxH  = fixedH > 0 ? fixedH : 400;
    this._cardWidth  = cardWidth;
    this._cardHeight = videoMaxH;

    var root = this._campaignRoot || this._wrap;
    root.style.setProperty('--sv-card-width', cardWidth + 'px');
    root.style.setProperty('--sv-card-video-max-height', videoMaxH + 'px');
    root.style.setProperty('--sv-visual-gap', (layout.gap || 16) + 'px');

    var halfW = cardWidth / 2;

    if (this._isCarousel) {
      var totalH = this._estimateCardHeight(cardWidth, videoMaxH);
      this._cardTotalHeight = totalH;
      root.style.setProperty('--sv-carousel-total-height', totalH + 'px');
      this._cards.forEach(function (card) {
        card.style.width       = cardWidth + 'px';
        card.style.height      = totalH + 'px';
        card.style.marginLeft  = (-halfW) + 'px';
      });
      if (this._track) this._track.style.minHeight = totalH + 'px';
      if (this._wrap) this._wrap.style.minHeight = (totalH + 95) + 'px';
      return;
    }

    this._cards.forEach(function (card) {
      card.style.width       = cardWidth + 'px';
      card.style.height      = 'auto';
      card.style.marginLeft  = (-halfW) + 'px';
    });

    var self = this;
    requestAnimationFrame(function () {
      self._updateTrackHeight();
    });
  },

  _isRingLayout: function () {
    var layout = (this._config && this._config.layout) || 'coverflow';
    return isRingLayout(layout);
  },

  _shouldSinglePlayback: function () {
    return this._isRingLayout() || this._playActiveOnly;
  },

  _applyPlaybackConfig: function (layoutSizes) {
    var cfg = this._config || {};
    var cv  = (layoutSizes && layoutSizes.cardsVisible) || cfg.cardsVisible || 5;
    var isCarousel = this._isCarousel || cfg.layout === 'carousel';

    /* Carousel (Tolstoy-style): always play the center card. */
    if (isCarousel) {
      this._playbackSlot   = Math.floor((cv + 1) / 2);
      this._playbackOffset = 0;
    } else {
      var slot = parseInt(cfg.playbackSlot, 10);
      if (isNaN(slot) || slot < 1) slot = Math.floor((cv + 1) / 2);
      slot = Math.min(slot, cv);
      this._playbackSlot   = slot;
      this._playbackOffset = playbackOffsetFromSlot(slot, cv);
    }

    var start = parseInt(cfg.startingVideo, 10);
    this._startingVideoIndex = (isNaN(start) || start < 1) ? 0 : start - 1;
  },

  _initialActiveIndex: function () {
    return activeIndexForStartingVideo(
      this._startingVideoIndex,
      this._playbackOffset,
      this._realCount
    );
  },

  _estimateCardHeight: function (cardWidth, videoMaxH) {
    var cfg = this._config || {};
    var shape = cfg.cardShape || 'portrait';
    var ratio = shape === 'square' ? 1 : shape === 'landscape' ? 16 / 9 : 9 / 16;
    var videoH = Math.round(cardWidth / ratio);
    /* card_height drives the video area height directly (matches CSS), so
       the slider always changes the visible card height. */
    if (videoMaxH > 0) videoH = videoMaxH;
    return videoH + 72;
  },

  _updateTrackHeight: function () {
    var tallest = 0;
    var self = this;
    this._cards.forEach(function (card) {
      tallest = Math.max(tallest, card.offsetHeight || 0);
    });
    if (tallest < 1) {
      tallest = this._estimateCardHeight(this._cardWidth, this._cardHeight);
    }
    this._cardTotalHeight = tallest;
    if (this._track) this._track.style.minHeight = tallest + 'px';
    if (this._wrap) this._wrap.style.minHeight = (tallest + 95) + 'px';
  },

  _scaleForOffset: function (offset) {
    if (this._isCarousel) return 1;
    return offset === 0 ? 1.15 : Math.max(0.75, 1.0 - offset * 0.12);
  },

  _layoutStep: function (scaleA, scaleB, cardWidth, visualGap) {
    return visualGap + cardWidth * (scaleA + scaleB) / 2;
  },

  _circularOffset: function (i, active, total) {
    if (total <= 1) return 0;
    var diff = i - active;
    var half = total / 2;
    if (diff > half) diff -= total;
    else if (diff < -half) diff += total;
    else if (diff === half) diff = -half;
    return diff;
  },

  _xForOffset: function (offset, cardWidth, visualGap) {
    if (offset === 0) return 0;
    var x   = 0;
    var dir = offset > 0 ? 1 : -1;
    var n   = Math.abs(offset);
    var s;
    for (s = 0; s < n; s++) {
      x += dir * this._layoutStep(
        this._scaleForOffset(s),
        this._scaleForOffset(s + 1),
        cardWidth,
        visualGap
      );
    }
    return x;
  },

  _visualGap: function () {
    var root = this._campaignRoot || this._wrap;
    var gap  = parseFloat(window.getComputedStyle(root).getPropertyValue('--sv-visual-gap'));
    if (isNaN(gap)) {
      gap = parseFloat(window.getComputedStyle(this._track).getPropertyValue('--sv-visual-gap'));
    }
    return isNaN(gap) ? 16 : gap;
  },

  _realIndexForCard: function (card) {
    var videoId = card.dataset.videoId;
    if (!videoId) return -1;
    for (var i = 0; i < this._realCards.length; i++) {
      if (this._realCards[i].dataset.videoId === videoId) return i;
    }
    return -1;
  },

  _oneStep: function () {
    return this._layoutStep(
      this._scaleForOffset(0),
      this._scaleForOffset(1),
      this._cardWidth,
      this._visualGap()
    );
  },

  _ringDistance: function (from, to, total) {
    if (total <= 1) return 0;
    var diff = to - from;
    var half = total / 2;
    if (diff > half) diff -= total;
    else if (diff < -half) diff += total;
    else if (diff === half) diff = -half;
    return diff;
  },

  _cancelFocusStep: function () {
    if (this._focusStepTimer) {
      clearTimeout(this._focusStepTimer);
      this._focusStepTimer = null;
    }
    if (this._slideTimer) {
      clearTimeout(this._slideTimer);
      this._slideTimer = null;
    }
    if (this._wrap) {
      this._wrap.classList.remove('is-sliding');
    }
    if (this._shouldSinglePlayback() && this._autoplay) {
      this._syncActivePlayback();
    }
    this._focusTarget = null;
  },

  /* Ring-wrap cards must snap — animating X across the loop looks like
     they travel behind the carousel from left back to right.
     Applies to coverflow and carousel (loop kept, wrap jumps are instant). */
  _shouldSnapCard: function (oldPos, newX, newOffset, focusJump) {
    if (!oldPos) return false;
    var xDelta = Math.abs(newX - oldPos.x);
    var stepThreshold = this._oneStep() * 1.35;

    if (oldPos.offset * newOffset < 0 &&
        Math.abs(oldPos.offset) + Math.abs(newOffset) >= this._cards.length - 1) {
      return true;
    }
    if (xDelta > stepThreshold) return true;
    if (!this._isCarousel && focusJump) {
      var halfRing = this._oneStep() * Math.max(1, this._cards.length / 2 - 0.5);
      return xDelta > halfRing;
    }
    return false;
  },


  /* ── Core coverflow update ───────────────────────────────────────── */

  /**
   * Position each card on a circular ring around the active index.
   * One next/prev step slides every card smoothly to its new ring slot.
   * Cards that wrap around the ring snap instantly (no cross-carousel drift).
   *
   * @param {boolean} animate — false on init/resize to skip the transition
   * @param {{ focusJump?: boolean }} [options]
   */
  _update: function (animate, options) {
    var focusJump   = options && options.focusJump;
    var cards       = this._cards;
    var activeIndex = this._activeIndex;
    var cardWidth   = this._cardWidth;
    var total       = cards.length;
    var visualGap   = this._visualGap();
    var cardTrans   = animate
      ? (this._isCarousel
        ? 'transform 0.45s cubic-bezier(0.25, 0.1, 0.25, 1)'
        : 'transform 0.45s cubic-bezier(0.25, 0.1, 0.25, 1), box-shadow 0.45s ease')
      : 'none';

    this._track.style.transition = 'none';
    this._track.style.transform  = 'none';

    cards.forEach(function (card, i) {
      var ringOffset = this._circularOffset(i, activeIndex, total);
      var absOffset  = Math.abs(ringOffset);
      var scale      = this._scaleForOffset(absOffset);
      var x          = this._xForOffset(ringOffset, cardWidth, visualGap);
      var oldPos     = this._prevPositions[i];
      var snap       = animate && this._shouldSnapCard(oldPos, x, ringOffset, focusJump);
      var trans      = snap ? 'none' : cardTrans;
      var transform  = 'translate3d(' + x + 'px, 0, 0) scale(' + scale + ')';

      if (this._isCarousel) {
        if (snap) {
          card.style.transition = 'none';
          card.style.opacity    = '0';
          card.style.transform  = transform;
        } else {
          card.style.transition = trans;
          card.style.transform  = transform;
        }
      } else if (animate && !snap && oldPos) {
        var oldScale = this._scaleForOffset(Math.abs(oldPos.offset));
        card.style.transition = 'none';
        card.style.transform  = 'translate3d(' + oldPos.x + 'px, 0, 0) scale(' + oldScale + ')';
        void card.offsetHeight;
        card.style.transition = cardTrans;
        card.style.transform  = transform;
      } else {
        card.style.transition = trans;
        card.style.transform  = transform;
      }
      card.style.zIndex     = String(total - absOffset);
      card.classList.toggle('is-active', ringOffset === 0);
      /* Carousel: center card is always the playing card (Tolstoy spotlight). */
      var playOffset = this._isCarousel ? 0 : this._playbackOffset;
      card.classList.toggle('is-playing', ringOffset === playOffset);
      card.setAttribute('tabindex', ringOffset === 0 ? '0' : '-1');
      card.setAttribute('aria-selected', ringOffset === 0 ? 'true' : 'false');

      if (ringOffset !== 0) {
        var drawer = card.querySelector('[data-sv-panel-drawer]');
        var toggle = card.querySelector('[data-sv-panel-toggle]');
        if (drawer && drawer.classList.contains('is-open')) {
          drawer.classList.remove('is-open');
          drawer.setAttribute('aria-hidden', 'true');
          if (toggle) {
            toggle.setAttribute('aria-expanded', 'false');
            toggle.classList.remove('is-open');
          }
        }
      }

      if (snap) {
        if (!this._isCarousel) card.style.opacity = '0';
        requestAnimationFrame(function () {
          card.style.opacity = '1';
        });
      } else if (card.style.opacity !== '1') {
        card.style.opacity = '1';
      }

      this._prevPositions[i] = { x: x, offset: ringOffset };
    }, this);

    var section = this._wrap.closest('section') || document;
    var prevBtn = section.querySelector('[data-sv-carousel-prev]');
    var nextBtn = section.querySelector('[data-sv-carousel-next]');
    var single  = total <= 1;
    if (prevBtn) prevBtn.disabled = single;
    if (nextBtn) nextBtn.disabled = single;

    if (this._shouldSinglePlayback() && this._autoplay) {
      var self = this;
      if (animate && this._isCarousel) {
        if (!this._wrap.classList.contains('is-sliding')) {
          this._wrap.classList.add('is-sliding');
        }
        this._syncActivePlayback();
        this._warmCarouselVideos();
        clearTimeout(this._slideTimer);
        this._slideTimer = setTimeout(function () {
          self._wrap.classList.remove('is-sliding');
          self._slideTimer = null;
        }, 460);
      } else {
        this._syncActivePlayback();
        this._warmCarouselVideos();
      }
    } else {
      this._warmCarouselVideos();
    }
  },


  /* ── Navigation ─────────────────────────────────────────────────── */

  _stepBy: function (dir) {
    if (this._realCount <= 1) return;
    if (dir > 0) {
      this._activeIndex = (this._activeIndex + 1) % this._realCount;
    } else {
      this._activeIndex = (this._activeIndex - 1 + this._realCount) % this._realCount;
    }
    this._update(true);
  },

  _focusCard: function (index) {
    if (index === this._activeIndex && !this._focusStepTimer) return;

    this._cancelFocusStep();

    var total = this._realCount;
    var dist  = this._ringDistance(this._activeIndex, index, total);

    /* Carousel: multi-card jumps step one slot at a time so edge cards don't snap */
    if (this._isCarousel && Math.abs(dist) > 1) {
      this._isAnimating = true;
      this._focusTarget = index;
      this._focusCardStepped(index);
      return;
    }

    if (this._isAnimating) return;
    this._isAnimating = true;
    this._activeIndex = index;
    this._update(true, { focusJump: Math.abs(dist) > 1 });
    this._afterSlide();
  },

  _focusCardStepped: function (targetIndex) {
    var self    = this;
    var total   = this._realCount;
    var STEP_MS = 450;

    if (this._activeIndex === targetIndex) {
      this._focusTarget = null;
      this._afterSlide();
      return;
    }

    var dist = this._ringDistance(this._activeIndex, targetIndex, total);
    var step = dist > 0 ? 1 : -1;
    this._activeIndex = (this._activeIndex + step + total) % total;
    this._update(true);

    this._focusStepTimer = setTimeout(function () {
      self._focusStepTimer = null;
      self._focusCardStepped(targetIndex);
    }, STEP_MS);
  },

  /**
   * Open the active card's video in the carousel lightbox popup.
   * Popup is ~80vh tall; width follows the video aspect ratio.
   */
  _openPopup: function (card) {
    var videoEl = card.querySelector('[data-sv-card-video]');
    if (!videoEl) return;

    var videoId = card.dataset.videoId;
    var meta    = this._registry[videoId] || {};

    ShoppableVideoCarouselPopup.open({
      src:        videoEl.currentSrc || videoEl.src,
      poster:     videoEl.poster,
      videoId:    videoId,
      title:      meta.title,
      influencer: meta.influencerName,
    });
  },

  _afterSlide: function () {
    var self = this;
    clearTimeout(this._animTimer);
    this._animTimer = setTimeout(function () {
      self._isAnimating = false;
    }, 460);
  },

  _next: function () {
    if (this._realCount <= 1 || this._isAnimating) return;
    this._isAnimating = true;
    this._stepBy(1);
    this._afterSlide();
  },

  _prev: function () {
    if (this._realCount <= 1 || this._isAnimating) return;
    this._isAnimating = true;
    this._stepBy(-1);
    this._afterSlide();
  },


  /* ── Autoplay ───────────────────────────────────────────────────── */

  /* Single IntersectionObserver on the wrap — when the section scrolls
     into viewport, play videos (all cards, or active card only).
     preload="none" in the HTML means the browser downloads nothing until play(). */
  _setupLazyLoad: function () {
    var self = this;

    if (this._shouldSinglePlayback()) {
      this._viewportObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            self._syncActivePlayback();
          } else {
            self._pauseAll();
          }
        });
      }, { threshold: 0.1 });
      this._viewportObserver.observe(this._wrap);
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.disconnect(); /* fire once */
        self._playAll();
      });
    }, { threshold: 0.1 });
    observer.observe(this._wrap);
  },

  _pauseAll: function () {
    this._realCards.forEach(function (card) {
      var video = card.querySelector('[data-sv-card-video]');
      if (video) video.pause();
    });
  },

  /* Preload metadata for nearby cards — required when there is no poster thumbnail. */
  _warmCarouselVideos: function () {
    if (!this._isRingLayout()) return;
    var self = this;
    var activeIndex = this._activeIndex;
    var total = this._realCards.length;
    var warmRadius = Math.max(2, Math.ceil((this._cardsVisible || 5) / 2) + 1);
    this._realCards.forEach(function (card, i) {
      var offset = Math.abs(self._circularOffset(i, activeIndex, total));
      if (offset > warmRadius) return;
      var video = card.querySelector('[data-sv-card-video]');
      if (!video) return;
      if (!video.getAttribute('src') && !video.src) return;
      if (video.readyState > 0) return;
      try {
        if (video.preload === 'none') video.preload = 'metadata';
        video.load();
      } catch (e) { /* ignore */ }
    });
  },

  _ensureVideoPlayable: function (video) {
    if (!video) return;
    var src = (video.getAttribute('src') || video.src || '').trim();
    if (!src) return;
    var attrSrc = video.getAttribute('src') || '';
    if (attrSrc !== src) {
      video.setAttribute('src', src);
    }
    if (video.readyState === 0) {
      try {
        if (video.preload === 'none') video.preload = 'metadata';
        video.load();
      } catch (e) { /* ignore */ }
    }
  },

  _syncActivePlayback: function () {
    if (!this._autoplay) return;
    if (!this._shouldSinglePlayback()) return;
    var loopVideos     = this._config ? this._config.loopVideos : true;
    var defaultMuted   = this._defaultMuted;
    var autoscroll     = this._config ? this._config.autoscroll : false;
    var activeIndex    = this._activeIndex;
    var total          = this._realCards.length;
    var playbackOffset = this._isCarousel ? 0 : this._playbackOffset;
    var self = this;
    this._realCards.forEach(function (card, i) {
      var video = card.querySelector('[data-sv-card-video]');
      if (!video) return;
      var ringOffset = self._circularOffset(i, activeIndex, total);
      var isPlaying  = ringOffset === playbackOffset;
      card.classList.toggle('is-playing', isPlaying);
      if (isPlaying) {
        video.loop  = loopVideos;
        video.muted = defaultMuted;
        self._ensureVideoPlayable(video);
        var playPromise = video.play();
        if (playPromise && playPromise.catch) {
          playPromise.catch(function () {
            /* Retry once media can start after load without poster */
            var retry = function () {
              video.removeEventListener('loadeddata', retry);
              video.removeEventListener('canplay', retry);
              video.play().catch(function () {});
            };
            video.addEventListener('loadeddata', retry, { once: true });
            video.addEventListener('canplay', retry, { once: true });
          });
        }
        attachProgressBar(video, card);
        var videoId = card.dataset.videoId;
        if (videoId) self._updateMuteIcon(videoId, defaultMuted);
        if (autoscroll && !video._svAutoscrollAttached) {
          video._svAutoscrollAttached = true;
          video.addEventListener('ended', function () {
            if (card.classList.contains('is-playing')) self._next();
          });
        }
      } else if (!video.paused) {
        video.pause();
      }
    });
  },

  _playAll: function () {
    var defaultMuted = this._defaultMuted;
    var loopVideos   = this._config ? this._config.loopVideos : true;
    var autoscroll   = this._config ? this._config.autoscroll : false;
    var self = this;
    this._realCards.forEach(function (card) {
      var video = card.querySelector('[data-sv-card-video]');
      if (!video) return;
      video.loop  = loopVideos;
      video.muted = defaultMuted;
      video.play().catch(function () {});
      attachProgressBar(video, card);

      if (autoscroll && !video._svAutoscrollAttached) {
        video._svAutoscrollAttached = true;
        video.addEventListener('ended', function () {
          if (card.classList.contains('is-active')) self._next();
        });
      }
    });

    /* Sync mute button icons to match the initial muted state */
    this._realCards.forEach(function (card) {
      var videoId = card.dataset.videoId;
      if (videoId) self._updateMuteIcon(videoId, defaultMuted);
    });
  },


  /* ── Mute toggle ────────────────────────────────────────────────── */

  /**
   * Toggle mute for one video, enforcing mutual exclusivity for audio.
   *
   * Mute-exclusivity logic:
   *   If the target video is currently MUTED and the user wants to unmute it:
   *     1. Mute ALL other videos and update their icons.
   *     2. Unmute the target and update its icon.
   *   If the target video is currently UNMUTED:
   *     Simply mute it — no effect on others.
   *
   * This ensures at most one video ever plays audio at a time.
   */
  _toggleMute: function (videoId) {
    var self        = this;
    var targetVideo = this._track.querySelector('[data-sv-card-video][data-video-id="' + videoId + '"]');
    if (!targetVideo) return;

    var willUnmute = targetVideo.muted;

    if (willUnmute) {
      /* Mute every other video before unmuting this one */
      this._track.querySelectorAll('[data-sv-card-video]').forEach(function (v) {
        if (v.dataset.videoId !== videoId) {
          v.muted = true;
          self._updateMuteIcon(v.dataset.videoId, true);
        }
      });
      targetVideo.muted = false;
      this._updateMuteIcon(videoId, false);
    } else {
      targetVideo.muted = true;
      this._updateMuteIcon(videoId, true);
    }
  },

  /* Swap the muted/unmuted SVG icons for a given video card. */
  _updateMuteIcon: function (videoId, isMuted) {
    var btn = this._track.querySelector('[data-sv-mute-btn][data-video-id="' + videoId + '"]');
    if (!btn) return;
    var mutedIcon   = btn.querySelector('.sv-mute-icon--muted');
    var unmutedIcon = btn.querySelector('.sv-mute-icon--unmuted');
    if (mutedIcon)   mutedIcon.style.display   = isMuted ? '' : 'none';
    if (unmutedIcon) unmutedIcon.style.display = isMuted ? 'none' : '';
  },
};


// ─────────────────────────────────────────────────────────────────────────────
// Registry + feed builder
// ─────────────────────────────────────────────────────────────────────────────

/* Parse every <script class="sv-video-data"> block into a registry map once.
   Registry shape: { [videoHandle]: videoDataObject } */
function buildVideoRegistry() {
  var registry = {};
  document.querySelectorAll('script.sv-video-data[data-video-id]').forEach(function (el) {
    try {
      var data = JSON.parse(el.textContent);
      if (data.videoUrl) data.videoUrl = String(data.videoUrl).trim();
      if (data.thumbnail) data.thumbnail = String(data.thumbnail).trim();
      else data.thumbnail = '';
      registry[el.dataset.videoId] = data;
    } catch (e) {
      console.warn('[ShoppableVideo] Could not parse data for', el.dataset.videoId, e);
    }
  });
  return registry;
}

/* Build an ordered array of video objects from a feed container (used by modal nav). */
function buildFeedArray(feedEl, registry) {
  var seen = {}, feed = [];
  feedEl.querySelectorAll('[data-video-id]').forEach(function (el) {
    var id = el.dataset.videoId;
    if (registry[id] && !seen[id]) { seen[id] = true; feed.push(registry[id]); }
  });
  return feed;
}


// ─────────────────────────────────────────────────────────────────────────────
// IntersectionObserver: landing page vertical feed
// ─────────────────────────────────────────────────────────────────────────────

function setupLandingAutoplay() {
  document.querySelectorAll('[data-sv-campaign][data-sv-layout="vertical_feed"]').forEach(function (root) {
    var cfg   = readCampaignConfig(root);
    var items = root.querySelectorAll('[data-sv-landing-item]');
    if (!items.length || !cfg.autoplay) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target.querySelector('[data-sv-landing-video]');
        if (!video) return;
        if (entry.isIntersecting) {
          video.loop = cfg.loopVideos;
          video.play().catch(function () {});
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.7 });

    items.forEach(function (item) { observer.observe(item); });
  });
}


// ─────────────────────────────────────────────────────────────────────────────
// Grid layout — mute, panel, autoplay, optional modal on click
// ─────────────────────────────────────────────────────────────────────────────

function syncGridSinglePlayback(cards, cfg) {
  var best = null;
  var bestRatio = 0;
  cards.forEach(function (card) {
    var rect = card.getBoundingClientRect();
    if (!rect.height) return;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var visible = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
    var ratio = visible / rect.height;
    if (ratio > bestRatio) {
      bestRatio = ratio;
      best = card;
    }
  });

  cards.forEach(function (card) {
    var video = card.querySelector('[data-sv-card-video]');
    if (!video) return;
    var shouldPlay = card === best && bestRatio >= 0.2;
    card.classList.toggle('is-playing', shouldPlay);
    if (shouldPlay) {
      video.loop  = cfg.loopVideos;
      video.muted = cfg.defaultMuted;
      video.play().catch(function () {});
      attachProgressBar(video, card);
    } else {
      video.pause();
    }
  });
}

function setupGridInteractions(registry) {
  document.querySelectorAll('[data-sv-campaign] [data-sv-grid]').forEach(function (gridWrap) {
    var root   = gridWrap.closest('[data-sv-campaign]');
    var cfg    = readCampaignConfig(root);
    var cards  = Array.from(gridWrap.querySelectorAll('[data-sv-grid-item]'));

    if (cfg.autoplay) {
      if (cfg.playActiveOnly) {
        var syncPlayback = function () { syncGridSinglePlayback(cards, cfg); };
        var observer = new IntersectionObserver(function () {
          syncPlayback();
        }, { threshold: [0, 0.2, 0.35, 0.5, 0.75, 1] });
        cards.forEach(function (card) { observer.observe(card); });
        window.addEventListener('scroll', syncPlayback, { passive: true });
        syncPlayback();
      } else {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target.querySelector('[data-sv-card-video]');
            if (!video) return;
            if (entry.isIntersecting) {
              video.loop  = cfg.loopVideos;
              video.muted = cfg.defaultMuted;
              video.play().catch(function () {});
              attachProgressBar(video, entry.target);
            } else {
              video.pause();
            }
          });
        }, { threshold: 0.35 });
        cards.forEach(function (card) { observer.observe(card); });
      }
    }

    gridWrap.addEventListener('click', function (e) {
      var muteBtn = e.target.closest('[data-sv-mute-btn]');
      if (muteBtn) {
        e.stopPropagation();
        toggleGridMute(gridWrap, muteBtn.dataset.videoId);
        return;
      }

      var panelBtn = e.target.closest('[data-sv-panel-toggle]');
      if (panelBtn) {
        var panel  = panelBtn.closest('[data-sv-product-panel]');
        var drawer = panel && panel.querySelector('[data-sv-panel-drawer]');
        if (!drawer) return;
        var isOpen = drawer.classList.contains('is-open');
        drawer.classList.toggle('is-open', !isOpen);
        drawer.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
        panelBtn.setAttribute('aria-expanded', String(!isOpen));
        panelBtn.classList.toggle('is-open', !isOpen);
        return;
      }

      if (!cfg.openOnClick) return;

      var card = e.target.closest('[data-sv-grid-item]');
      if (!card || !e.target.closest('.sv-card-video-wrap')) return;
      if (e.target.closest('[data-sv-product-panel]')) return;

      var videoId = card.dataset.videoId;
      if (!videoId || !registry[videoId]) return;

      var feed = buildFeedArray(root, registry);
      var startIndex = feed.findIndex(function (v) { return v.id === videoId; });
      if (startIndex === -1) startIndex = 0;

      ShoppableVideoPlayer.open(registry[videoId], feed, startIndex, readModalOpts(root));
    });
  });
}

function toggleGridMute(gridWrap, videoId) {
  var targetVideo = gridWrap.querySelector('[data-sv-card-video][data-video-id="' + videoId + '"]');
  if (!targetVideo) return;

  var willUnmute = targetVideo.muted;

  if (willUnmute) {
    gridWrap.querySelectorAll('[data-sv-card-video]').forEach(function (v) {
      if (v.dataset.videoId !== videoId) {
        v.muted = true;
        updateGridMuteIcon(gridWrap, v.dataset.videoId, true);
      }
    });
    targetVideo.muted = false;
    updateGridMuteIcon(gridWrap, videoId, false);
  } else {
    targetVideo.muted = true;
    updateGridMuteIcon(gridWrap, videoId, true);
  }
}

function updateGridMuteIcon(gridWrap, videoId, isMuted) {
  var btn = gridWrap.querySelector('[data-sv-mute-btn][data-video-id="' + videoId + '"]');
  if (!btn) return;
  var mutedIcon   = btn.querySelector('.sv-mute-icon--muted');
  var unmutedIcon = btn.querySelector('.sv-mute-icon--unmuted');
  if (mutedIcon)   mutedIcon.style.display   = isMuted ? '' : 'none';
  if (unmutedIcon) unmutedIcon.style.display = isMuted ? 'none' : '';
}


// ─────────────────────────────────────────────────────────────────────────────
// Delegated click: modal trigger for PDP / landing cards
// ─────────────────────────────────────────────────────────────────────────────

/* [data-sv-card] — vertical feed product chips and grid modal triggers.
   (Carousel cards use [data-sv-carousel-card] instead.) */
function attachModalCardDelegation(registry) {
  document.addEventListener('click', function (e) {
    var card = e.target.closest('[data-sv-card]');
    if (!card) return;

    var videoId = card.dataset.videoId;
    var feedId  = card.dataset.feed;
    if (!videoId || !registry[videoId]) return;

    var feedEl = feedId
      ? document.querySelector('[data-sv-feed="' + feedId + '"]')
      : card.closest('[data-sv-feed]');

    var feed = [registry[videoId]], startIndex = 0;
    if (feedEl) {
      feed = buildFeedArray(feedEl, registry);
      startIndex = feed.findIndex(function (v) { return v.id === videoId; });
      if (startIndex === -1) startIndex = 0;
    }

    ShoppableVideoPlayer.open(registry[videoId], feed, startIndex, readModalOpts(card));
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var card = document.activeElement && document.activeElement.closest('[data-sv-card]');
    if (!card) return;
    e.preventDefault();
    card.click();
  });
}


// ─────────────────────────────────────────────────────────────────────────────
// Delegated click: Add to Cart — document-wide
// ─────────────────────────────────────────────────────────────────────────────

/* Panel drawer + modal product cards — [data-sv-add-to-cart] → theme cart flow */
function attachAtcDelegation() {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-sv-add-to-cart]');
    if (!btn || btn.disabled) return;

    e.preventDefault();
    e.stopPropagation();

    var variantId = btn.dataset.variantId;
    var videoId   = btn.dataset.videoId;
    ShoppableVideoCart.addWithTheme(variantId, 1, {
      triggerEl: btn,
      videoId:   videoId,
    });
  });
}


// ─────────────────────────────────────────────────────────────────────────────
// Boot
// ─────────────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', function () {
  var registry = buildVideoRegistry();

  /* Bail early if no shoppable video content on this page */
  if (!Object.keys(registry).length) return;

  attachModalCardDelegation(registry); /* landing / grid / legacy PDP cards */
  attachAtcDelegation();               /* carousel panels + modal */
  setupLandingAutoplay();              /* vertical feed */
  setupGridInteractions(registry);     /* grid layout */

  /* Initialise every coverflow carousel found on the page */
  document.querySelectorAll('[data-sv-carousel]').forEach(function (wrapEl) {
    ShoppableVideoCarousel.init(wrapEl, registry);
  });
});

/* Expose on window for console debugging */
window.ShoppableVideoPlayer          = ShoppableVideoPlayer;
window.ShoppableVideoCart            = ShoppableVideoCart;
window.ShoppableVideoAnalytics       = ShoppableVideoAnalytics;
window.ShoppableVideoCarousel        = ShoppableVideoCarousel;
window.ShoppableVideoCarouselPopup   = ShoppableVideoCarouselPopup;
